import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";

const eur = (c) => `€${(c / 100).toLocaleString("en-US")}`;
const MAX_POLLS = 8;

export default function PreorderSuccess() {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [status, setStatus] = useState(sessionId ? "checking" : "done");
  const [info, setInfo] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!cleared.current) { clearCart(); cleared.current = true; }
  }, [clearCart]);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/preorders/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setStatus("done"); return; }
        attempts += 1;
        if (attempts >= MAX_POLLS) { setStatus("pending"); return; }
        timer = setTimeout(poll, 2000);
      } catch { setStatus("error"); }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const reference = info?.reference || ref;

  return (
    <div className="min-h-[85svh] flex items-center justify-center pt-32 pb-24" data-testid="preorder-success-page">
      <div className="container-lux max-w-xl text-center">
        {status === "checking" && (
          <>
            <p className="label-mono text-smoke mb-4">Confirming your pre-order</p>
            <div className="w-10 h-10 border border-blood border-t-transparent rounded-full animate-spin mx-auto" />
          </>
        )}
        {(status === "done" || status === "pending") && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="label-mono text-blood mb-6">Pre-order received</p>
            <h1 className="heading-serif text-4xl md:text-5xl mb-8 leading-tight">
              Your obsession begins here.
            </h1>
            <p className="text-ivory/75 font-sans leading-relaxed mb-8">
              Your pre-order has been received. A confirmation email is on its way with your reference and details.
            </p>
            {reference && (
              <div className="border border-blood/40 inline-block px-8 py-5 mb-8">
                <p className="label-mono text-smoke mb-2">Your reference</p>
                <p className="font-mono text-blood text-lg tracking-[0.2em]" data-testid="preorder-reference">{reference}</p>
              </div>
            )}
            {info?.estimated_shipping ? (
              <p className="label-mono text-smoke mb-8">Estimated dispatch · {info.estimated_shipping}</p>
            ) : null}
            {status === "pending" && (
              <p className="label-mono text-smoke mb-8">Payment is still being verified — check your email shortly.</p>
            )}
            <div>
              <Link to="/collection" data-testid="preorder-continue" className="btn-relic border-ivory bg-ivory text-ink hover:bg-blood hover:border-blood hover:text-ivory">
                Return to the House
              </Link>
            </div>
          </motion.div>
        )}
        {status === "error" && (
          <>
            <p className="label-mono text-blood mb-6">Something went astray</p>
            <h1 className="heading-serif text-3xl md:text-4xl mb-6">We could not confirm this pre-order</h1>
            <Link to="/collection" className="btn-relic border-ivory/40 text-ivory hover:border-blood">Return to the Collection</Link>
          </>
        )}
      </div>
    </div>
  );
}
